import type { ActionSource } from './schema';
import { actionWithDatapoint } from 'owa-analytics-actions';
import { mutatorAction, action } from 'satcheljs';
import type {
    AssetId,
    NewOutlookSpaceAsset,
    OutlookSpace,
    OutlookSpaceAsset,
    OutlookSpaceAssetSyncResult,
    ProjectId,
    TimeStreamAsset,
    PatchOutlookSpaceAsset,
} from 'owa-timestream-types';
import getAsset from './selectors/getAsset';

export const setProjects = actionWithDatapoint(
    'OUTLOOKSPACES_PROJECTS_LOADED',
    (sourceMailbox: string, outlookSpaces: OutlookSpace[], appName?: string) => ({
        sourceMailbox,
        outlookSpaces,
        appName,
    }),
    ({ sourceMailbox, outlookSpaces }) => ({
        customData: { outlookSpaces_1: outlookSpaces.length, hasSourceMailbox: !!sourceMailbox },
        options: { isCore: true },
    })
);

export const setProject = action(
    'OUTLOOKSPACES_ASSETS_LOADED',
    (
        projectId: ProjectId,
        outlookSpace: OutlookSpace,
        outlookSpaceAssets?: OutlookSpaceAsset[]
    ) => ({
        projectId,
        outlookSpace,
        outlookSpaceAssets,
    })
);

export const processOutlookSpaceAssetSyncResult = actionWithDatapoint(
    'OUTLOOKSPACES_ASSET_SYNCRESULT',
    (projectId: ProjectId, syncResult: OutlookSpaceAssetSyncResult) => ({
        projectId,
        syncResult,
    }),
    ({ syncResult }) => ({
        customData: syncResult.value.reduce(
            (a, { type }) => {
                a[type] = (a[type] || 0) + 1;
                return a;
            },
            {
                assetCount_1: syncResult.value.length,
                deleteCount_2: syncResult.deleted ? syncResult.deleted.length : 0,
            }
        ),
        options: { isCore: true },
    })
);

export const addClientSideAsset = action(
    'addClientSideAsset',
    (
        projectId: ProjectId,
        clientAssetId: AssetId,
        asset: NewOutlookSpaceAsset,
        actionSource: ActionSource
    ) => ({
        projectId,
        clientAssetId,
        asset,
        actionSource,
    })
);

export const upsertClientAssets = action(
    'upsertAssets',
    (projectId: ProjectId, outlookSpaceAssets: OutlookSpaceAsset[]) => ({
        projectId,
        outlookSpaceAssets,
    })
);

export const deleteClientSideAssets = action(
    'deleteClientSideAsset',
    (projectId: ProjectId, assetIds: AssetId[]) => ({ projectId, assetIds })
);

export const updateCachedAsset = mutatorAction(
    'updateCachedAsset',
    (
        projectId: ProjectId,
        assetId: AssetId,
        updates: { lastModifiedDateTime: string } | TimeStreamAsset | PatchOutlookSpaceAsset
    ) => {
        const asset = getAsset(projectId, assetId);
        if (asset) {
            Object.keys(updates).forEach(key => {
                if (asset[key] != updates[key]) {
                    asset[key] = updates[key];
                }
            });
        }
    }
);
